import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Grid, LinearProgress, Typography } from '@mui/material';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as ReUploadSuccessIcon } from 'common/assets/images/circle-checkmark-success.svg';
import { ReactComponent as UploadSuccessIcon } from 'common/assets/images/circle-checkmark.svg';
import { ReactComponent as DocTextIcon } from 'common/assets/images/document-img.svg';
import { ReactComponent as ErrorIcon } from 'common/assets/images/error.svg';
import { ReactComponent as ReUploadIcon } from 'common/assets/images/regenerate.svg';
import { BAD_REQUEST_MSG, TOAST_SEVERITY } from 'common/constants';
import {
	RE_UPLOAD_BTN_LABEL,
	RE_UPLOAD_STATUS,
	UPLOAD_ERROR_LABEL,
} from 'common/constants/AskRiskLinkConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import {
	setActiveChatSessionUploadedFiles,
	setActiveChatSessionUploadingFiles,
	setReUploadHistory,
} from 'common/store/reducer/commonReducer';
import { getFormattedSize } from 'common/utils/utilFunctions';

import { useUploadForm } from 'hooks/useUploadForm';

import {
	IFileInfo,
	IUploadingFileStatus,
} from 'features/Reporting/api/type-def';

import {
	deleteLatestFailedFilesUploadedDocument,
	getFileFromBlob,
	getSessionData,
} from '../api';
import { IUploadResponse } from '../api/type-def';
import {
	fileUploadStartHandler,
	filterUploadFinishedFiles,
	getFailedUploadedFileNames,
	getRefreshedReUploadHistory,
	getReUploadSuccessFile,
	OPEN_RISK_GPT,
	updatedFailedFileData,
} from '../OpenRiskGPTUtil';

import styles from './ReUploadDocument.module.scss';

const { icon } = styles;
const { RE_UPLOAD_LABEL, UPLOADING_LABEL, RE_UPLOAD_SUCCESS_LABEL, CANCEL } =
	RE_UPLOAD_BTN_LABEL;
const { UPLOAD_SUCCESS, UPLOAD_FAILED, RE_UPLOAD_SUCCESS } = RE_UPLOAD_STATUS;
const { OK } = HTTP_STATUS_CODES;
const { ERROR } = TOAST_SEVERITY;
const { NETWORK_ISSUE_MESSAGE } = OPEN_RISK_GPT;

const ReUploadDocument = (props: {
	uploadUrl: string | undefined;
	handleToastChange: (message: string, severity: string) => void;
	sessionId: string;
}) => {
	const { uploadUrl, handleToastChange, sessionId } = props;
	const dispatch = useDispatch();

	const [isUploading, setIsUploading] = useState(false);

	const reUploadHistory = useSelector(
		(state: RootState) => state.common.reUploadHistory,
	);
	const activeChatSessionUploadedFiles = useSelector(
		(state: RootState) => state.common.activeChatSessionUploadedFiles,
	);
	const activeChatSessionUploadingFiles = useSelector(
		(state: RootState) => state.common.activeChatSessionUploadingFiles,
	);
	const uploadedDocumentProgress = useSelector(
		(state: RootState) => state.common.uploadedDocumentProgress,
	);

	const { uploadSuccessFiles, uploadFailedFiles, reuploadSuccessFiles } =
		reUploadHistory as IUploadingFileStatus;

	const {
		uploadForm,
		isSuccess,
		multiFileUploadFailed,
		uploadResponse,
		reUploadFlag,
	} = useUploadForm(uploadUrl as string, dispatch);

	const uploadProgress: any = useMemo(
		() => uploadedDocumentProgress,
		[uploadedDocumentProgress],
	);

	const getFilteredFilesInfo = (data: any) =>
		data?.length > 0 ? filterUploadFinishedFiles(data) : [];
	const getSessionDetails = async () => {
		const sessionDetail = {
			isGetSession: false,
			uploadedFile: [],
		};
		const response: any = await getSessionData(sessionId);
		if (response?.statusCode === OK) {
			const docInfoFromApi = response.responseBody?.documents;
			const uploadedFile = getFilteredFilesInfo(docInfoFromApi);
			dispatch(setActiveChatSessionUploadedFiles(uploadedFile));
			sessionDetail.isGetSession = true;
			sessionDetail.uploadedFile = uploadedFile;
		}
		return sessionDetail;
	};

	const checkFailedFilesExist = () => {
		if (uploadFailedFiles?.length > 0) {
			// call api to delete files
			deleteLatestFailedUploadedDocument();
		} else {
			onReUploadClickHandler();
		}
	};

	const deleteLatestFailedUploadedDocument = async () => {
		const failedFiles = getFailedUploadedFileNames(uploadFailedFiles);

		if (failedFiles?.length > 0) {
			setIsUploading(true);
			const resp = await deleteLatestFailedFilesUploadedDocument(
				sessionId,
				failedFiles,
			);

			if (resp?.statusCode === OK) {
				onReUploadClickHandler();
			} else {
				handleToastChange(NETWORK_ISSUE_MESSAGE, ERROR);
				setIsUploading(false);
			}
		}
	};
	const onReUploadClickHandler = useCallback(async () => {
		setIsUploading(true);
		const { isGetSession, uploadedFile } = await getSessionDetails();
		if (isGetSession) {
			const { failedFileArr, newReUploadHistory } = getRefreshedReUploadHistory(
				uploadedFile,
				reUploadHistory,
			);
			const uploadedFileArr = [...uploadedFile] as IFileInfo[];
			// get re-upload success file from uploading files
			const uploadSuccessFilesArr = getReUploadSuccessFile(
				activeChatSessionUploadingFiles,
				uploadedFileArr,
			);
			// if we have failed upload file then upload them
			if (failedFileArr.length > 0) {
				const updatedArray = fileUploadStartHandler(failedFileArr);
				dispatch(
					setReUploadHistory({
						...(newReUploadHistory as IUploadingFileStatus),
						uploadFailedFiles: updatedArray,
						uploadSuccessFiles: uploadSuccessFilesArr,
					}),
				);
				const formData = new FormData();
				const fileData: File[] = [];
				await Promise.all(
					failedFileArr.map(async (file) => {
						const { name, blobURL, type } = file;
						// retrive the file from blob
						const fileObj = await getFileFromBlob(blobURL, name, type);
						formData.append('file', fileObj);
						fileData.push(fileObj);
					}),
				);
				dispatch(setActiveChatSessionUploadingFiles(failedFileArr));
				await uploadForm(formData, true, fileData, true);
				// if we have no failed upload file means files are reuploaded successfully
			} else {
				dispatch(
					setReUploadHistory({
						timeStamp: String(new Date()),
						uploadSuccessFiles: [],
						uploadFailedFiles: [],
						reuploadSuccessFiles: uploadSuccessFilesArr,
					}),
				);
				setIsUploading(false);
			}
		} else {
			setIsUploading(false);
			handleToastChange(NETWORK_ISSUE_MESSAGE, ERROR);
		}
	}, [reUploadHistory]);

	const onFileUploadSuccess = (uploadResponse: any[]) => {
		// successfully re-upload file will display in the re-upload success part
		const reuploadSuccessFiles: IFileInfo[] = [];
		uploadResponse.forEach((res: IUploadResponse) => {
			if (res.status === OK) {
				const fileInfo: IFileInfo = {
					name: res.data.detail[0].name,
					size: res.data.detail[0].size,
					id: res.data.detail[0].id,
				};
				reuploadSuccessFiles.push(fileInfo);
			}
		});
		dispatch(
			setReUploadHistory({
				timeStamp: String(new Date()),
				uploadSuccessFiles,
				uploadFailedFiles: [],
				reuploadSuccessFiles,
			}),
		);
		// clear uploading file
		dispatch(setActiveChatSessionUploadingFiles([]));
		// add successful uploaded file to uploaded file
		dispatch(
			setActiveChatSessionUploadedFiles([
				...activeChatSessionUploadedFiles,
				...reuploadSuccessFiles,
			]),
		);
	};
	const onDeleteFailedFileHandler = () => {
		dispatch(
			setReUploadHistory({
				...(reUploadHistory as IUploadingFileStatus),
				uploadFailedFiles: [],
			}),
		);
	};
	const onFileUploadFailed = (uploadResponse: any[]) => {
		// add successfully re-upload file to uploadSuccessFiles
		let failedFileArr = [...uploadFailedFiles];
		const successFileArr: IFileInfo[] = [];
		uploadResponse.forEach((res: IUploadResponse) => {
			if (res.status === OK) {
				const fileInfo: IFileInfo = {
					name: res.data.detail[0].name,
					size: res.data.detail[0].size,
					id: res.data.detail[0].id,
				};
				successFileArr.push(fileInfo);
				// delete success uploaded file from uploading file list
				failedFileArr = failedFileArr.filter(
					(failedFile) => failedFile.name !== fileInfo.name,
				);
			}
		});
		const updatedArray = fileUploadStartHandler(failedFileArr);
		dispatch(
			setReUploadHistory({
				timeStamp: String(new Date()),
				uploadSuccessFiles: successFileArr,
				uploadFailedFiles: updatedArray,
				reuploadSuccessFiles: [],
			}),
		);
		// set failed uploaded file as uploading file
		dispatch(setActiveChatSessionUploadingFiles(failedFileArr));
		// add successful uploaded file to uploaded file
		dispatch(
			setActiveChatSessionUploadedFiles([
				...activeChatSessionUploadedFiles,
				...successFileArr,
			]),
		);
		const errResponse = uploadResponse.filter((res) => res.status !== OK);
		if (errResponse.length > 0) {
			if (errResponse[0].data.response) {
				const errorMsg = errResponse[0].data.response.data.error;
				handleToastChange(
					errorMsg.length > 100 ? BAD_REQUEST_MSG : errorMsg,
					ERROR,
				);
			} else {
				handleToastChange(NETWORK_ISSUE_MESSAGE, ERROR);
			}
		}
	};

	useEffect(() => {
		if (isSuccess) {
			onFileUploadSuccess(uploadResponse);
		}
		if (multiFileUploadFailed) {
			onFileUploadFailed(uploadResponse);
		}
		setIsUploading(false);
	}, [reUploadFlag]);

	useEffect(() => {
		const updatedUploadFailedFiles = updatedFailedFileData(
			uploadFailedFiles,
			uploadProgress,
		);
		dispatch(
			setReUploadHistory({
				...(reUploadHistory as IUploadingFileStatus),
				uploadFailedFiles: updatedUploadFailedFiles,
			}),
		);
	}, [uploadProgress]);

	const getUploadRecordComp = (fileList: any[], fileStatus: RE_UPLOAD_STATUS) =>
		fileList.map((file: any) => (
			<Grid
				container
				flexDirection="column"
				justifyContent="center"
				className={styles['re-upload-record']}
				key={file?.name}
			>
				<Grid container justifyContent="space-between">
					<Box className={styles['record-content']}>
						<DocTextIcon className={icon} />
						<Typography className={styles['file-name']}>
							{file?.name}
						</Typography>
						<Typography className={styles['file-size']}>
							{getFormattedSize(file?.size)}
						</Typography>
					</Box>
					<Box className={styles['record-content']}>
						{!isUploading && fileStatus === UPLOAD_FAILED && (
							<>
								<Box className={styles['record-content-error-text']}>
									{UPLOAD_ERROR_LABEL}
								</Box>
								<ErrorIcon className={icon} />
							</>
						)}
						{fileStatus !== UPLOAD_FAILED && (
							<UploadSuccessIcon className={icon} />
						)}
					</Box>
				</Grid>
				{isUploading && fileStatus === UPLOAD_FAILED && (
					<Box className={styles['linear-progress']}>
						<LinearProgress
							variant="determinate"
							value={file?.progress}
							className={styles['filedropbox-progressbar']}
						/>
					</Box>
				)}
			</Grid>
		));

	const getUploadRecordList = (
		fileList: any[],
		fileStatus: RE_UPLOAD_STATUS,
	) => (
		<Box className={styles['re-upload-wrapper']} key={fileStatus}>
			{getUploadRecordComp(fileList, fileStatus)}
			{fileStatus !== UPLOAD_SUCCESS &&
				(fileStatus === RE_UPLOAD_SUCCESS ? (
					<Grid
						container
						className={styles['re-upload-success-container']}
						direction="row"
						alignItems="center"
					>
						<ReUploadSuccessIcon className={icon} />
						<Box className={styles['re-upload-success-text']}>
							{RE_UPLOAD_SUCCESS_LABEL}
						</Box>
					</Grid>
				) : (
					<div className={styles['re-upload-button-container']}>
						<LoadingButton
							loadingPosition="start"
							variant="text"
							disableRipple
							disableTouchRipple
							loading={isUploading}
							onClick={checkFailedFilesExist}
							startIcon={<ReUploadIcon className={icon} />}
							className={styles['re-upload-button']}
						>
							<span>{isUploading ? UPLOADING_LABEL : RE_UPLOAD_LABEL}</span>
						</LoadingButton>
						<LoadingButton
							variant="text"
							disableRipple
							disableTouchRipple
							loading={false}
							className={styles['re-upload-button']}
							onClick={onDeleteFailedFileHandler}
							data-testid="cancel-model"
							disabled={isUploading}
						>
							{CANCEL}
						</LoadingButton>
					</div>
				))}
		</Box>
	);

	return (
		<Grid
			container
			display="flex"
			direction="column"
			data-testid="reupload-container"
		>
			{uploadSuccessFiles &&
				uploadSuccessFiles.length > 0 &&
				getUploadRecordList(uploadSuccessFiles, UPLOAD_SUCCESS)}
			{uploadFailedFiles &&
				uploadFailedFiles.length > 0 &&
				getUploadRecordList(uploadFailedFiles, UPLOAD_FAILED)}
			{reuploadSuccessFiles &&
				reuploadSuccessFiles.length > 0 &&
				getUploadRecordList(reuploadSuccessFiles, RE_UPLOAD_SUCCESS)}
		</Grid>
	);
};

export default ReUploadDocument;
